import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRightIcon as ArrowRight, EnvelopeSimpleIcon as EnvelopeSimple, CheckCircleIcon as CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Landing-page newsletter capture.
 *
 * Editorial styling — cream paper tier with a gold hairline above
 * the footer. One-line input + submit. Idempotent on the backend
 * so re-subscribing a known email never throws a friction-causing
 * "you already subscribed" error.
 *
 * Conversion notes (why this is in the design):
 *   - Sits IMMEDIATELY above the legal footer so visitors who
 *     scroll past everything land on a conversion offer instead
 *     of a privacy policy.
 *   - Value-prop is concrete (verification standards, new species,
 *     industry analysis), not vague ("get our newsletter").
 *   - Success state is celebratory but not blocking — same height
 *     as the form, so the layout doesn't jump.
 */
export default function JournalNewsletterCapture({ source = "landing-footer" }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success
  const [submittedEmail, setSubmittedEmail] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    // Cheap front-end shape check — backend uses Pydantic EmailStr
    // for the real validation, this just stops obviously-broken
    // submissions before they round-trip.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error("That email doesn't look right — double-check?");
      return;
    }
    setStatus("loading");
    try {
      const { data } = await api.post("/public/journal/subscribe", {
        email: trimmed,
        source,
      });
      setSubmittedEmail(trimmed);
      setStatus("success");
      if (data?.already_subscribed) {
        toast.success("You're already on the list — we'll keep you posted.");
      } else {
        toast.success("Welcome to the Journal. First issue lands next.");
      }
      setEmail("");
    } catch (err) {
      setStatus("idle");
      toast.error("Couldn't subscribe right now — try again in a minute?");
    }
  };

  return (
    <section
      className="relative bg-cream/60 border-y border-[#E2DAC2] py-14 md:py-20"
      data-testid="landing-journal-newsletter"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gold-hairline" />
      <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
        <p className="text-[10px] font-bold uppercase tracking-[0.32em] text-gold-dark mb-3">
          The Armstrong Journal
        </p>
        <h2 className="font-spectral text-3xl md:text-5xl tracking-tight text-ink leading-[1.1]">
          What &lsquo;verified&rsquo; means, in your inbox.
        </h2>
        <p className="mt-4 text-[14.5px] md:text-[15.5px] text-[#3F3F3F] max-w-xl mx-auto leading-relaxed">
          New verification standards, breed-specific market analysis, kennel
          tours, scam alerts, and the occasional editorial worth reading.
          One thoughtful issue every other week. Unsubscribe in one click.
        </p>

        {status === "success" ? (
          <div
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-ink text-white px-6 py-3 shadow-[0_6px_20px_rgba(0,0,0,0.25)]"
            data-testid="landing-journal-success"
          >
            <CheckCircle size={18} weight="fill" className="text-gold-light" />
            <span className="text-[13.5px]">
              <strong className="font-bold">{submittedEmail}</strong> &mdash; you&rsquo;re on the list.
            </span>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 max-w-xl mx-auto"
            data-testid="landing-journal-form"
          >
            <div className="relative flex-1">
              <EnvelopeSimple
                size={16}
                weight="duotone"
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B6B6B]"
              />
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                placeholder="you@kennel.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={status === "loading"}
                aria-label="Email address"
                data-testid="landing-journal-email"
                className="w-full rounded-full bg-white border border-[#D9CFB0] focus:border-gold-dark focus:outline-none focus:ring-2 focus:ring-gold/30 h-12 pl-11 pr-5 text-[15px] text-ink placeholder-[#9A9A9A] transition-colors"
              />
            </div>
            <Button
              type="submit"
              disabled={status === "loading"}
              className="rounded-full bg-ink hover:bg-crimson text-white h-12 px-7 font-semibold whitespace-nowrap shadow-[0_8px_24px_rgba(0,0,0,0.15)]"
              data-testid="landing-journal-submit"
            >
              {status === "loading" ? "Subscribing…" : (
                <>Subscribe <ArrowRight size={16} className="ml-2" /></>
              )}
            </Button>
          </form>
        )}

        <p className="mt-5 text-[11px] text-[#6B6B6B] max-w-md mx-auto leading-relaxed">
          No spam, ever. We don&rsquo;t sell your address. Sent through Resend
          on Armstrong K9 Registry&rsquo;s verified sender domain.
        </p>
      </div>
    </section>
  );
}
